import { ReactNode } from 'react'
import { SxProps, TableCell, Theme, Tooltip } from '@mui/material'
import DiscrepancyPill from './DiscrepancyPill'

type DiscrepancyNumberCellProps = {
  children: ReactNode
  hasDiscrepancy?: boolean
  discrepancyLabel?: string
  fallbackTooltip?: string
  emphasized?: boolean
  onDiscrepancyClick?: (anchorEl: HTMLElement) => void
  onHoverChange?: (hovering: boolean) => void
  sx?: SxProps<Theme>
}

/**
 * Right-aligned numeric line-item cell. When it carries a discrepancy it wraps
 * the value in a tinted pill and becomes a button: clicking it opens the
 * breakdown popover (via `onDiscrepancyClick`, passed the cell element as the
 * anchor). With no discrepancy it shows `fallbackTooltip` (e.g. "Remaining")
 * on hover, or nothing. `emphasized` is the treatment mirrored onto every cell
 * in the discrepancy's match group while any one of them is hovered or its
 * popover is open; `onHoverChange` reports the pointer entering/leaving so the
 * parent can drive that group emphasis.
 */
export default function DiscrepancyNumberCell({
  children,
  hasDiscrepancy,
  discrepancyLabel,
  fallbackTooltip,
  emphasized,
  onDiscrepancyClick,
  onHoverChange,
  sx,
}: DiscrepancyNumberCellProps) {
  if (hasDiscrepancy) {
    return (
      <TableCell
        align="right"
        data-has-discrepancy="true"
        role="button"
        tabIndex={0}
        aria-label={discrepancyLabel}
        onClick={(e) => onDiscrepancyClick?.(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onDiscrepancyClick?.(e.currentTarget)
          }
        }}
        onMouseEnter={() => onHoverChange?.(true)}
        onMouseLeave={() => onHoverChange?.(false)}
        sx={{ py: 0.5, cursor: 'pointer', ...sx }}
      >
        <DiscrepancyPill hasDiscrepancy emphasized={emphasized}>
          {children}
        </DiscrepancyPill>
      </TableCell>
    )
  }

  return (
    <Tooltip
      title={fallbackTooltip || ''}
      disableHoverListener={!fallbackTooltip}
      enterDelay={200}
      disableInteractive
      arrow
      placement="top"
    >
      <TableCell align="right" sx={{ py: 0.5, ...sx }}>
        {children}
      </TableCell>
    </Tooltip>
  )
}
